import { useAuth0 } from '@auth0/auth0-react';
import { Switch } from '@odekoteam/doppio';
import { Flex } from 'theme-ui';
import { GetLocation } from 'src/graphql/types/delivery';
import { useAppDispatch } from 'src/hooks/store';
import { ModalType, showModal } from 'src/state/slices/modal';
import { hasAdminRole } from 'src/utils/authRoles';

export const customerPickupSwitchId = (locationId: string): string =>
  `customer-pickup-switch-${locationId}`;

// Flagging is not a straight write: turning it on queues a backend sweep of the
// location's upcoming Lobby stops and can be rejected outright for recent
// driven deliveries. So the switch only ever opens the dialog, and moves when
// the server confirms, which is what makes a failure leave it where it was.
export default function CustomerPickupSwitch({
  location,
  contextId,
}: {
  location: GetLocation;
  contextId?: string;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const { user } = useAuth0();
  // updateLocationsCustomerPickup is declared authorized_roles: %w[admin], so
  // bp-admin (which useMetrobiAdmin admits) would get a server rejection.
  const isAdmin = hasAdminRole(user as Record<string, unknown> | undefined);
  // The doppio Switch is only controlled while isChecked is a boolean; an
  // undefined makes it uncontrolled, so it would move on click with nothing
  // wired behind it.
  const isChecked = Boolean(location.customerPickup);

  return (
    <div>
      <Flex>
        <Switch
          id={customerPickupSwitchId(location.id)}
          accessibilityLabel={`Customer pickup for ${location.businessName}`}
          isChecked={isChecked}
          isDisabled={!isAdmin}
          onCheckedChange={(customerPickup): void => {
            dispatch(
              showModal({
                type: ModalType.CustomerPickupToggle,
                dismissButtonLabel: 'Cancel',
                variables: {
                  locationId: location.id,
                  businessName: location.businessName,
                  customerPickup,
                  contextId,
                  returnFocusId: customerPickupSwitchId(location.id),
                },
              }),
            );
          }}
        />
        <span sx={{ marginLeft: '12px', variant: 'text.paragraphLgDefault' }}>
          {isChecked ? 'On' : 'Off'}
        </span>
      </Flex>
      {!isAdmin && (
        <div sx={{ variant: 'text.paragraphSmDefault' }}>
          Admin access required to edit
        </div>
      )}
    </div>
  );
}
